import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../Utils/Axios';
import { useRegion } from '../context/RegionContext';
import {
  getRegionCountryOptions,
  getRegionCountryRecord,
  getRegionCityOptions,
  getRegionFlagUrl,
  getRegionLocationLabel,
  getRegionStateLabel,
  getRegionStateOptions,
  getRegionStateRecord,
} from '../Utils/region';
import './JobList.css';

const resolveHospitalLogo = (logo) => logo || '/assets/images/bg/company-logo/company-01.png';

const JobList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [keyword, setKeyword] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const { region } = useRegion();

  const stateLabel = useMemo(() => getRegionStateLabel(region), [region]);
  const locationLabel = useMemo(() => getRegionLocationLabel(region), [region]);
  const countryOptions = useMemo(() => (region === 'af' ? getRegionCountryOptions(region) : []), [region]);
  const selectedCountryRecord = useMemo(
    () => (region === 'af' ? getRegionCountryRecord(region, selectedCountry) : null),
    [region, selectedCountry]
  );
  const stateNames = useMemo(() => getRegionStateOptions(region, selectedCountryRecord), [region, selectedCountryRecord]);
  const selectedStateObj = useMemo(
    () => getRegionStateRecord(region, selectedState, selectedCountryRecord),
    [region, selectedState, selectedCountryRecord]
  );
  const cities = useMemo(
    () => getRegionCityOptions(region, selectedStateObj, selectedCountryRecord),
    [region, selectedStateObj, selectedCountryRecord]
  );
  const categoryOptions = useMemo(
    () => categories.map((item) => item.name).filter(Boolean),
    [categories]
  );
  const subcategoryOptions = useMemo(
    () => {
      const names = subcategories.map((item) => item.name).filter(Boolean);
      const other = [];
      const rest = [];
      names.forEach((name) => {
        if (String(name).trim().toLowerCase() === 'other') {
          other.push(name);
        } else {
          rest.push(name);
        }
      });
      return [...rest, ...other];
    },
    [subcategories]
  );
  const specialtyOptions = useMemo(
    () => specialties.map((item) => item.name).filter(Boolean),
    [specialties]
  );
  const subcategoryLiveCounts = useMemo(() => {
    const counts = {};
    jobs.forEach((job) => {
      const key = String(job.subcategory || '').trim();
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [jobs]);

  const activeStateForBanner = useMemo(() => {
    if (selectedState) return selectedState;

    const q = String(keyword || '').trim().toLowerCase();
    if (!q) return '';

    const match = stateNames.find((name) => q.includes(String(name).trim().toLowerCase()));
    return match || '';
  }, [selectedState, keyword, stateNames]);

  const activeStateFlagUrl = useMemo(() => {
    if (region === 'af' && selectedCountryRecord) {
      return getRegionFlagUrl(region, selectedCountryRecord);
    }
    const stateObj = getRegionStateRecord(region, activeStateForBanner, selectedCountryRecord);
    return getRegionFlagUrl(region, stateObj);
  }, [activeStateForBanner, region, selectedCountryRecord]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await api.get('/api/categories');
        setCategories(res.data?.categories || []);
      } catch (error) {
        setCategories([]);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    const loadSubcategories = async () => {
      if (!selectedCategory) {
        setSubcategories([]);
        setSelectedSubcategory('');
        setSpecialties([]);
        setSelectedSpecialty('');
        return;
      }
      try {
        const res = await api.get('/api/categories/subcategories', {
          params: { category: selectedCategory },
        });
        setSubcategories(res.data?.subcategories || []);
      } catch (error) {
        setSubcategories([]);
        setSelectedSubcategory('');
      }
    };

    loadSubcategories();
  }, [selectedCategory]);

  useEffect(() => {
    const loadSpecialties = async () => {
      if (!selectedSubcategory) {
        setSpecialties([]);
        setSelectedSpecialty('');
        return;
      }
      try {
        const res = await api.get('/api/categories/specialties', {
          params: { subcategory: selectedSubcategory },
        });
        setSpecialties(res.data?.specialties || []);
      } catch (error) {
        setSpecialties([]);
        setSelectedSpecialty('');
      }
    };

    loadSpecialties();
  }, [selectedSubcategory]);

  const fetchJobs = async (filters) => {
    setLoadingJobs(true);
    try {
      const res = await api.get('/api/hospital/public-jobs', {
        params: {
          country: filters.country || undefined,
          state: filters.state || undefined,
          city: filters.city || undefined,
          zip: filters.zip || undefined,
          category: filters.category || undefined,
          subcategory: filters.subcategory || undefined,
          specialty: filters.specialty || undefined,
          q: filters.q || undefined,
        },
      });
      setJobs(res.data?.jobs || []);
    } catch (error) {
      setJobs([]);
    } finally {
      setLoadingJobs(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    const filters = {
      country: searchParams.get('country') || '',
      state: searchParams.get('state') || '',
      city: searchParams.get('city') || '',
      zip: searchParams.get('zip') || '',
      category: searchParams.get('category') || '',
      subcategory: searchParams.get('subcategory') || '',
      specialty: searchParams.get('specialty') || '',
      q: searchParams.get('q') || '',
    };

    setSelectedCountry(filters.country);
    setSelectedState(filters.state);
    setSelectedCity(filters.city);
    setZipcode(filters.zip);
    setSelectedCategory(filters.category);
    setSelectedSubcategory(filters.subcategory);
    setSelectedSpecialty(filters.specialty);
    setKeyword(filters.q);
    fetchJobs(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    const params = {};
    if (region === 'af' && selectedCountry) params.country = selectedCountry;
    if (selectedState) params.state = selectedState;
    if (selectedCity) params.city = selectedCity;
    if (zipcode) params.zip = zipcode;
    if (selectedCategory) params.category = selectedCategory;
    if (selectedSubcategory) params.subcategory = selectedSubcategory;
    if (selectedSpecialty) params.specialty = selectedSpecialty;
    if (keyword) params.q = keyword;
    setSearchParams(params);
  };

  return (
    <div>
      <div className={`inner-banner ${activeStateForBanner ? 'has-state-flag-bg' : ''}`}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="banner-content text-center">
                {activeStateForBanner ? (
                  <div className="state-hero-flag-wrap" aria-hidden="true">
                    <div
                      className="state-hero-flag-layer one"
                      style={{ backgroundImage: `url(${activeStateFlagUrl})` }}
                    />
                    <div
                      className="state-hero-flag-layer two"
                      style={{ backgroundImage: `url(${activeStateFlagUrl})` }}
                    />
                    <div className="state-hero-flag-overlay" />
                  </div>
                ) : null}

                <h1 className="state-title-row">Job Listing</h1>
                <span />
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Home</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Job Listing
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="job-listing-area pt-120 mb-120">
        <div className="container">
          <div className="row g-lg-4 gy-5">
            <div className="col-lg-4 order-lg-1 order-2">
              <div className="job-sidebar">
                <div className="job-widget style-1 mb-20">
                  <div className="check-box-item">
                    <h5 className="job-widget-title">Job Filters</h5>
                    <form onSubmit={handleApplyFilters}>
                      <div className="mb-15">
                        <div className="form-inner">
                          <input
                            className="job-filter-textbox"
                            type="text"
                            placeholder="Search Jobs by Keyword/Req #"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="mb-15">
                        <div className="form-inner">
                          <input
                            className="job-filter-textbox"
                            type="text"
                            placeholder="Zip Code"
                            value={zipcode}
                            onChange={(e) => setZipcode(e.target.value.replace(/\D/g, ''))}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={10}
                          />
                        </div>
                      </div>
                      {region === 'af' ? (
                        <div className="mb-15">
                          <div className="form-inner" style={{ position: 'relative' }}>
                            <select
                              value={selectedCountry}
                              onChange={(e) => {
                                setSelectedCountry(e.target.value);
                                setSelectedState('');
                                setSelectedCity('');
                              }}
                              style={{
                                width: '100%',
                                height: '60px',
                                padding: '0 50px 0 20px',
                                background: '#fff',
                                border: '1px solid #DFE9E5',
                                borderRadius: '5px',
                                color: selectedCountry ? 'var(--title-color1)' : '#7C8A9B',
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                              }}
                            >
                              <option value="">Country</option>
                              {countryOptions.map((country) => (
                                <option key={country} value={country}>
                                  {country}
                                </option>
                              ))}
                            </select>
                            <span
                              style={{
                                position: 'absolute',
                                right: '25px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                              }}
                            >
                              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z"
                                  fill="#1C2120"
                                />
                              </svg>
                            </span>
                          </div>
                        </div>
                      ) : null}
                      <div className="mb-15">
                        <div className="form-inner" style={{ position: 'relative' }}>
                          <select
                            value={selectedState}
                            onChange={(e) => {
                              setSelectedState(e.target.value);
                              setSelectedCity('');
                            }}
                            disabled={region === 'af' && !selectedCountry}
                            style={{
                              width: '100%',
                              height: '60px',
                              padding: '0 50px 0 20px',
                              background: '#fff',
                              border: '1px solid #DFE9E5',
                              borderRadius: '5px',
                              color: selectedState ? 'var(--title-color1)' : '#7C8A9B',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              opacity: region === 'af' && !selectedCountry ? 0.6 : 1,
                              cursor: region === 'af' && !selectedCountry ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <option value="">{stateLabel}</option>
                            {stateNames.map((state) => (
                              <option key={state} value={state}>
                                {state}
                              </option>
                            ))}
                          </select>
                          <span
                            style={{
                              position: 'absolute',
                              right: '25px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              pointerEvents: 'none',
                            }}
                          >
                            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z" fill="#1C2120" />
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div className="mb-15">
                        <div className="form-inner" style={{ position: 'relative' }}>
                          <select
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                            disabled={!selectedState}
                            style={{
                              width: '100%',
                              height: '60px',
                              padding: '0 50px 0 20px',
                              background: '#fff',
                              border: '1px solid #DFE9E5',
                              borderRadius: '5px',
                              color: selectedCity ? 'var(--title-color1)' : '#7C8A9B',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              opacity: !selectedState ? 0.6 : 1,
                              cursor: !selectedState ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <option value="">City</option>
                            {cities.map((city) => (
                              <option key={city} value={city}>
                                {city}
                              </option>
                            ))}
                          </select>
                          <span
                            style={{
                              position: 'absolute',
                              right: '25px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              pointerEvents: 'none',
                            }}
                          >
                            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z" fill="#1C2120" />
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div className="mb-15">
                        <div className="form-inner" style={{ position: 'relative' }}>
                          <select
                            value={selectedCategory}
                            onChange={(e) => {
                              setSelectedCategory(e.target.value);
                              setSelectedSubcategory('');
                              setSelectedSpecialty('');
                            }}
                            style={{
                              width: '100%',
                              height: '60px',
                              padding: '0 50px 0 20px',
                              background: '#fff',
                              border: '1px solid #DFE9E5',
                              borderRadius: '5px',
                              color: selectedCategory ? 'var(--title-color1)' : '#7C8A9B',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                            }}
                          >
                            <option value="">Category</option>
                            {categoryOptions.map((name) => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                          <span
                            style={{
                              position: 'absolute',
                              right: '25px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              pointerEvents: 'none',
                            }}
                          >
                            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z" fill="#1C2120" />
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div className="mb-15">
                        <div className="form-inner" style={{ position: 'relative' }}>
                          <select
                            value={selectedSubcategory}
                            onChange={(e) => {
                              setSelectedSubcategory(e.target.value);
                              setSelectedSpecialty('');
                            }}
                            disabled={!selectedCategory}
                            style={{
                              width: '100%',
                              height: '60px',
                              padding: '0 50px 0 20px',
                              background: '#fff',
                              border: '1px solid #DFE9E5',
                              borderRadius: '5px',
                              color: selectedSubcategory ? 'var(--title-color1)' : '#7C8A9B',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              opacity: !selectedCategory ? 0.6 : 1,
                              cursor: !selectedCategory ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <option value="">Subcategory</option>
                            {subcategoryOptions.map((name) => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                          <span
                            style={{
                              position: 'absolute',
                              right: '25px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              pointerEvents: 'none',
                            }}
                          >
                            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z" fill="#1C2120" />
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div className="mb-15">
                        <div className="form-inner" style={{ position: 'relative' }}>
                          <select
                            value={selectedSpecialty}
                            onChange={(e) => setSelectedSpecialty(e.target.value)}
                            disabled={!selectedSubcategory}
                            style={{
                              width: '100%',
                              height: '60px',
                              padding: '0 50px 0 20px',
                              background: '#fff',
                              border: '1px solid #DFE9E5',
                              borderRadius: '5px',
                              color: selectedSpecialty ? 'var(--title-color1)' : '#7C8A9B',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              opacity: !selectedSubcategory ? 0.6 : 1,
                              cursor: !selectedSubcategory ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <option value="">Specialty</option>
                            {specialtyOptions.map((name) => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                          <span
                            style={{
                              position: 'absolute',
                              right: '25px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              pointerEvents: 'none',
                            }}
                          >
                            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z" fill="#1C2120" />
                            </svg>
                          </span>
                        </div>
                      </div>
                      <button
                        className="primry-btn-2 lg-btn text-center w-100"
                        type="submit"
                        disabled={loadingJobs}
                      >
                        {loadingJobs ? 'Filtering...' : 'Apply Filters'}
                      </button>
                    </form>

                    {subcategoryLiveCounts.length > 0 ? (
                      <div className="mt-25">
                        <h6 style={{ marginBottom: 12 }}>Live Jobs by Subcategory</h6>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {subcategoryLiveCounts.map((item) => (
                            <li
                              key={item.name}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: 8,
                                fontSize: 14,
                              }}
                            >
                              <span>{item.name}</span>
                              <strong>{item.count}</strong>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-8 order-lg-2 order-1">
              <div className="job-listing-wrrap">
                <div className="row g-4 mb-25">
                  <div className="col-lg-6 d-flex align-items-center">
                    <p className="show-item">Showing {jobs.length} job(s)</p>
                  </div>
                  <div className="col-lg-6 d-flex align-items-center justify-content-lg-end">
                    <div className="grid-select-area">
                      <div className="select-area">
                        <select className="select1" value="Sort By(Default)" readOnly>
                          <option>Sort By(Default)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  {loading ? (
                    <div className="col-lg-12">
                      <p>Loading jobs...</p>
                    </div>
                  ) : jobs.length === 0 ? (
                    <div className="col-lg-12 mb-30">
                      <div className="job-listing-card">
                        <div className="job-top">
                          <div className="job-list-content">
                            <div className="company-area">
                              <div className="company-details">
                                <div className="name-location">
                                  <h5>No jobs found for current filters.</h5>
                                  <p>{`Try another ${stateLabel.toLowerCase()}, city, zip, category, subcategory, or search keyword.`}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    jobs.map((job) => (
                      <div className="col-lg-12 mb-30" key={job._id}>
                        <div className="job-listing-card">
                          <div className="job-top">
                            <div className="job-list-content">
                              <div className="company-area">
                                <div className="logo">
                                  <img src={resolveHospitalLogo(job.hospitalLogo)} alt={job.hospitalName || 'Hospital'} />
                                </div>
                                <div className="company-details">
                                  <div className="name-location">
                                    <h5>
                                      <Link to={`/job-details/${job._id}`}>{job.title}</Link>
                                    </h5>
                                    <p>{job.hospitalName}</p>
                                  </div>
                                </div>
                              </div>
                              <div className="job-discription">
                                <ul>
                                  <li>
                                    <p>
                                      <span className="title">Salary:</span> ${job.salaryMin || 0}-${job.salaryMax || 0}{' '}
                                      <span className="time">Per Month</span>
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="title">Deadline:</span>{' '}
                                      {job.deadline ? new Date(job.deadline).toLocaleDateString() : '-'}
                                    </p>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                          <div className="job-type-apply">
                            <div className="job-type">
                              <span className="light-green">{job.employmentType || 'Full Time'}</span>
                              {job.category ? <span className="light-blue">{job.category}</span> : null}
                              {job.subcategory ? <span className="light-blue">{job.subcategory}</span> : null}
                              <span className="light-purple">
                              {region === 'af'
                                ? [job.city, job.state, job.country].filter(Boolean).join(', ') || `Unknown, ${locationLabel}`
                                : [job.city, job.state].filter(Boolean).join(', ') || `Unknown, ${locationLabel}`}
                              </span>
                            </div>
                            <div className="apply-btn">
                              <Link to={`/job-details/${job._id}`}>
                                <span>
                                  <img src="/assets/images/icon/apply-ellipse.svg" alt="" />
                                </span>
                                Apply Now
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobList;
